import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { AppState } from '../../app.service';
import { ToasterService } from '../../toaster.service';
import { MicroActionStateService } from '../../micro-action-chatbot-v1/micro-action-state.service';
import { HomeUiService } from '../home-ui.service';
import { I18n } from '../../i18n.service';

/** The chips whose flow lives outside the in-step screen itself. */
export type MicroActionHistoryIntent = 'commit' | 'openTool' | 'markCompleted';

/**
 * The action to open once a cycle switch has finished reloading the app.
 *
 * sessionStorage because the switch routes and rebuilds everything: no component that could hold it
 * survives the trip. Read and cleared by the Coach panel on the other side.
 */
export const OPEN_ACTION_AFTER_SWITCH_KEY = 'foozi_open_action_after_switch';

/** A ceiling on the modals a cycle switch will close, so a stack that will not empty cannot hang it. */
const MAX_MODAL_STACK = 10;

/** Runs one of those flows. `done` is the screen's own bookkeeping — call it only on success. */
export type MicroActionHistoryHandler = (done?: () => void) => any;

/**
 * One micro-action, opened from the History modal's Micro-actions tab.
 *
 * Hosts the real in-step screen (`app-micro-action-chatbot-v1`) rather than a summary of its own, so
 * a learner looking back at an action sees exactly what they saw in the step — the same intro, action
 * text, tool link, chips, practice attempts and Customize-with-Coach bar.
 *
 * The three chips that need flows outside that screen — calendar commit, document open, completion —
 * run through handlers the caller supplies, so they work here exactly as they do in the step and the
 * modal stays open while they do.
 */
@Component({
  selector: 'app-micro-action-history-modal',
  templateUrl: './micro-action-history-modal.component.html',
  styleUrls: ['./micro-action-history-modal.component.scss'],
})
export class MicroActionHistoryModalComponent implements OnInit {
  /** The action to open. */
  @Input() screenContentId: number | string;
  /** The history row that was tapped, used for the heading when the action is no longer in the path. */
  @Input() customization: any;

  /**
   * The three flows that live on whatever opened this modal. Supplied rather than reimplemented:
   * the Coach panel already runs all three for its own chips, against the same action state the
   * screen below publishes.
   */
  @Input() onCommit: MicroActionHistoryHandler;
  @Input() onOpenTool: MicroActionHistoryHandler;
  @Input() onMarkCompleted: MicroActionHistoryHandler;

  /** The step/screen wrapper `app-micro-action-chatbot-v1` expects. Null when it cannot be built. */
  habit: any = null;

  /**
   * Whether anything in here changed the row the History list is showing.
   *
   * Reported on dismiss so the list only re-reads when there is something to re-read. Looking at an
   * action and closing it is the common case, and it used to cost a request and a visible reload of
   * a list that had not changed.
   */
  private changed = false;

  constructor(
    private modalController: ModalController,
    private appState: AppState,
    private microActionStateService: MicroActionStateService,
    private toaster: ToasterService,
    public homeUiService: HomeUiService,
    public i18n: I18n,
  ) {}

  /**
   * Offers to move the learner to the cycle this action belongs to.
   *
   * The whole note is the control, not a word inside it: it is a small target at the foot of a
   * screen, often on a phone. Confirmed first because switching cycles is not a view change — it
   * writes the learner's active cycle on the server and reloads their home, progress and to-do
   * list, and they arrived here by tapping a row in a history list.
   */
  offerCycleSwitch(): void {
    if (!this.canSwitchCycle) { return; }
    const cycleId = this.rowField('cycleId', 'cycle_id');

    // Phrased the way the app asks everywhere else — "Would you like to…?" over the recommit
    // prompt's alert, "Do you want to…?" over the habit one — rather than as a bare "Switch to X?".
    const named = this.otherCycleName
      ? `${this.i18n.t('Would you like to switch to')} ${this.otherCycleName} ${this.i18n.t('cycle')}?`
      : this.i18n.t('Would you like to switch to that cycle?');
    // showConfirm, not a Yes-first variant: every other confirmation in the app — the to-do list's,
    // app.component's, the recommit prompt — puts No under the thumb, and a switch that rewrites the
    // learner's active cycle is not the one to break that habit on.
    this.toaster.showConfirm(
      `${named} `,
      () => this.switchToThisCycle(cycleId),
    );
  }

  /**
   * Moves to the cycle, remembering the action so it can be opened once the app has reloaded.
   *
   * The switch rebuilds the home screen under them, so the action cannot simply be re-opened here.
   * The intent outlives that, and the Coach panel picks it up on the other side.
   */
  private switchToThisCycle(cycleId: number | string): void {
    try {
      sessionStorage.setItem(OPEN_ACTION_AFTER_SWITCH_KEY, String(this.screenContentId));
    } catch {
      // Storage refused: the switch still happens, the learner just lands on their home screen.
    }
    this.homeUiService.switchCycle(cycleId, {
      // Only once the server has agreed to the switch. Closing first meant a request that failed
      // left the learner on a home screen they never asked for, with History gone and a pending
      // action that nothing would ever come to collect.
      onSwitched: () => this.dismissEverythingOnScreen(),
      onFailed: () => {
        try {
          sessionStorage.removeItem(OPEN_ACTION_AFTER_SWITCH_KEY);
        } catch {
          // Nothing stored it in the first place, then.
        }
        this.toaster.error(this.i18n.t('We could not switch cycles. Please try again.'));
      },
    });
  }

  /**
   * Closes this modal and everything it was opened over.
   *
   * Ionic presents modals outside the router outlet, so the reload the switch performs does NOT
   * take them with it. Left standing, this screen and the History list behind it would sit over the
   * new cycle still showing the old one's rows, and the Coach panel that comes back on the other
   * side would open a second History on top of them — which is exactly what it looked like.
   */
  private async dismissEverythingOnScreen(): Promise<void> {
    // getTop first: dismissing with nothing presented rejects.
    for (let guard = 0; guard < MAX_MODAL_STACK; guard += 1) {
      const top = await this.modalController.getTop();
      if (!top) { return; }
      // A modal that declines to close — canDismiss — would otherwise spin this forever.
      const dismissed = await top.dismiss().catch(() => false);
      if (dismissed === false) { return; }
    }
  }

  ngOnInit() {
    // Chips anchor to whichever action published state last, and the panel behind may still hold an
    // earlier one — clear first so this action's own state is the one that takes.
    this.microActionStateService.clear();
    this.habit = this.buildHabit(this.screenContentId);
    // Answered every time, not only when the fallback runs: left to the branch below, a second init
    // that found the action in the path would keep the previous one's answer and show the screen as
    // a record when it is nothing of the kind.
    this.fromAnotherCycle = false;
    if (!this.habit) {
      // Not in the cycle the learner is in now. The screen can still be shown from the history row
      // itself — see habitFromRow for what that can and cannot do.
      this.habit = this.habitFromRow(this.screenContentId);
      this.fromAnotherCycle = !!this.habit && this.belongsToAnotherCycle();
      // An action the ACTIVE cycle no longer holds — removed, or republished under a new screen
      // content id. There is no cycle to send them to and nothing to open, so it stays what it is:
      // a record of something that is not there any more.
      if (!this.fromAnotherCycle) { this.habit = null; }
    }
  }

  /**
   * Whether the note can actually take the learner to that cycle.
   *
   * A row with no cycle id on it — one written before the field existed — can still be shown, but
   * there is nothing to switch to. Without this the bar was a button that did nothing, under a line
   * telling them to click it.
   */
  get canSwitchCycle(): boolean {
    if (!this.isReadOnly) { return false; }
    return !!this.rowField('cycleId', 'cycle_id') && !!this.appState.get('currentUser')?.id;
  }

  /**
   * Whether the row says this action belongs to a cycle other than the one the learner is in.
   *
   * The path not holding an action is not proof that it belongs elsewhere: one removed from the
   * active cycle, or republished under a new screen content id, is missing from it too. Read as
   * cross-cycle, that offered the learner a switch to the cycle they were already in.
   *
   * Unanswerable where the row carries no cycle id — rows written before that field existed — and
   * those keep the read-only render they have always had: no cycle id also means offerCycleSwitch
   * has nothing to switch to, so the most that can be wrong is a note that cannot name the cycle.
   */
  private belongsToAnotherCycle(): boolean {
    const rowCycle = this.rowField('cycleId', 'cycle_id');
    if (rowCycle === null || rowCycle === undefined || rowCycle === '') { return true; }
    const activeCycle = this.appState.get('currentPath')?.id;
    if (activeCycle === null || activeCycle === undefined) { return true; }
    return String(rowCycle) !== String(activeCycle);
  }

  /**
   * True when this action is being shown from its history row because the path has no copy of it.
   *
   * Everything the screen reads is keyed by screen_content_id, so the wording, the customisation
   * thread and the practice attempts render exactly as they do in the step — none of that knows
   * what a cycle is. The row now carries the rest too, so this says which cycle the learner is
   * looking at rather than what they cannot do in it.
   */
  fromAnotherCycle = false;

  /**
   * An action from another cycle is shown, not worked on.
   *
   * The row now carries everything a write needs — step and screen ids, and the switches that draw
   * the controls — so this is not a gap in what the client knows. It is what the rest of the system
   * can honour: the To-Do list renders the active cycle's actions only, so a commit made here would
   * be written, confirmed, and then appear in neither cycle's list; and a tool belonging to another
   * cycle cannot be fetched. A control that records something the learner can never see again is
   * worse than no control.
   *
   * Interim. When the To-Do list spans cycles and tools resolve across them, this becomes `false`
   * and the chips and composer come back with nothing else to change.
   */
  get isReadOnly(): boolean {
    return this.fromAnotherCycle;
  }

  /**
   * The action as it read before the learner changed anything, from the row rather than inferred.
   *
   * Only for an action from another cycle: where the path holds the action, its screen content IS
   * the published wording and nothing the row says beats it. Null when the row predates these
   * fields, which leaves the screen to work it out from the thread as before.
   */
  get originalAction(): { title: string; description: string } | null {
    if (!this.fromAnotherCycle) { return null; }
    const title = this.rowField('baseTitle', 'base_title');
    const description = this.rowField('baseDescription', 'base_description');
    if (!title && !description) { return null; }
    return { title: title || '', description: description || '' };
  }

  /** The cycle this action belongs to, when it is not the one the learner is in. */
  get otherCycleName(): string {
    if (!this.fromAnotherCycle) { return ''; }
    return String(this.rowField('cycleName', 'cycle_name') || '').trim();
  }

  /**
   * A field of the history row, under either spelling the server uses for it.
   *
   * /customizations answers with camelCase for the fields added for this screen — stepId, screenId,
   * canSchedule, toolLink — and snake_case for the ones that were always there: customization_id,
   * screen_content_id, action_status. Reading one spelling left every new field undefined, which
   * reads as "no ids, no switches": the screen opened read-only with no chips on it.
   */
  private rowField(camel: string, snake: string): any {
    const row = this.customization || {};
    return row[camel] ?? row[snake];
  }

  /** True when neither the path nor the history row can supply the action. */
  get isMissing(): boolean {
    return !this.habit;
  }

  get title(): string {
    return this.customization?.title || this.customization?.action_title || this.i18n.t('Micro-action');
  }

  /**
   * The screen wrapper, from the history row rather than from the path.
   *
   * The path holds the cycle the learner is in now, so an action practised in an earlier one is not
   * in it. Nothing the screen reads needs the cycle: its thread resolves by screen_content_id
   * (`habit.screen.id`), and so do its stored turns. The row carries the wording, and the flags that
   * would draw Commit, "I completed this" and Open Tool are deliberately absent — those write
   * against step_id and screen_id, which the row does not carry, so the chips they draw stay away
   * without a special case.
   */
  private habitFromRow(screenContentId: number | string): any | null {
    const scid = +screenContentId;
    const row = this.customization;
    if (!scid || !row) { return null; }
    return {
      screen: {
        id: scid,
        content: {
          title: row.title || row.action_title || '',
          text: row.description || row.action_description || '',
          // The action's own switches, as the step would supply them. Without these the chips they
          // draw stay away however complete the rest of the row is.
          canSchedule: !!this.rowField('canSchedule', 'can_schedule'),
          canCompleteNow: !!this.rowField('canCompleteNow', 'can_complete_now'),
          toolLink: this.rowField('toolLink', 'tool_link') || null,
          enableVoicePractice: !!this.rowField('enableVoicePractice', 'enable_voice_practice'),
        },
        userResponse: {
          userActionCompletion: String(row.action_status || '').toLowerCase() === 'completed',
        },
      },
      // What a commit, a completion and a saved customisation are written against. A row without
      // them can still be read — see isReadOnly.
      stepId: this.rowField('stepId', 'step_id') ?? undefined,
      screenId: this.rowField('screenId', 'screen_id') ?? undefined,
      screenContentId: scid,
    };
  }

  /**
   * The same object `Path.filterStepsByContentType('action')` produces for the daily-steps screens —
   * built here from the cached path, because a history row carries only a screen content id.
   */
  private buildHabit(screenContentId: number | string): any | null {
    const scid = +screenContentId;
    if (!scid) { return null; }

    for (const chapter of (this.appState.get('DailyStepsData') || [])) {
      for (const step of (chapter?.steps || [])) {
        for (const screen of (step?.screens || [])) {
          if (screen?.screenContents?.id === scid) {
            return {
              screen: screen.screenContents,
              step: step?.position,
              stepId: step?.step?.id,
              screenId: screen?.id,
              screenContentId: screen?.screenContents?.id,
              screenPosition: screen?.position,
              assets: screen?.assets,
            };
          }
        }
      }
    }
    return null;
  }

  /**
   * A chip whose flow lives outside the in-step screen. Runs the caller's handler and leaves this
   * modal open — closing it to commit or complete would throw the learner out of what they opened.
   */
  raise(intent: MicroActionHistoryIntent, done?: () => void) {
    const handler = intent === 'commit' ? this.onCommit
      : intent === 'openTool' ? this.onOpenTool
      : this.onMarkCompleted;
    // No handler means the modal was opened without a host to run the flow; the chip stays inert
    // rather than half-completing an action.
    if (!handler) { return; }
    // Opening a tool changes nothing about the row. A commit or a completion does, but only once it
    // has actually been written — `done` is called on success, and never on a failed one.
    if (intent === 'openTool') {
      handler(done);
      return;
    }
    handler(() => {
      this.changed = true;
      done?.();
    });
  }

  /** The action's wording was customised in here, which is what the row reports. */
  noteActionChanged(): void {
    this.changed = true;
  }

  close() {
    // Dismissing returns the learner to the History modal, which re-reads its list only if this
    // says there is a reason to.
    this.modalController.dismiss(this.changed ? { changed: true } : null);
  }
}
